import { useEffect, useState } from "react";
import { useLocation, useSearchParams, Link } from "react-router-dom";
import { useApp } from "../app/AppContext.jsx";

/**
 * The way in, and the only one.
 *
 * No password field, because there is no password: an address is typed, a link arrives, the
 * link is the proof. That is one screen instead of four — no sign-up form separate from
 * sign-in, no reset flow, no "we sent you a verification email" step after the fact — and it
 * is why the list this builds is a list of addresses that demonstrably work.
 *
 * Doubles as the wall itself. When a signed-out visitor asks for /stock the router renders
 * this in place of the page, carrying where they were going, so the link in the email brings
 * them back to the car they clicked rather than dumping them at the front door.
 */
export default function SignIn({ redirect }) {
  const { t, signIn, user } = useApp();
  const location = useLocation();
  const [params] = useSearchParams();

  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");      // idle | sending | sent | error
  const [problem, setProblem] = useState(params.get("error"));

  // Landing here with a session already — a second tab signed in, or the back button after
  // the link was clicked — should not present a form to someone who is through the door.
  useEffect(() => { if (user) setState("signedin"); }, [user]);

  const target = redirect ?? `${location.pathname}${location.search}`;

  const submit = async (e) => {
    e.preventDefault();
    setProblem(null);
    setState("sending");
    try {
      await signIn(email.trim(), target === "/signin" ? "/stock" : target);
      setState("sent");
    } catch (err) {
      // "We could not send it" is a different message from "that address is wrong", and a
      // buyer staring at an empty inbox deserves to know which one happened.
      setProblem(err?.body?.error === "invalid_email" ? "invalid_email"
        : err?.status === 429 ? "too_many"
        : "mail_failed");
      setState("error");
    }
  };

  if (state === "sent") {
    return (
      <div className="wrap page" style={{ maxWidth: 520 }}>
        <p className="label">{t("auth.sentLabel")}</p>
        <h1 className="h1" style={{ marginTop: 12 }}>{t("auth.sentTitle")}</h1>
        <p className="muted" style={{ marginTop: 16, lineHeight: 1.7 }}>
          {t("auth.sentBody", { email: email.trim() })}
        </p>
        <p className="tiny muted" style={{ marginTop: 20, lineHeight: 1.7 }}>{t("auth.sentSpam")}</p>
        <button className="btn btn--ghost btn--sm" style={{ marginTop: 24 }} onClick={() => setState("idle")}>
          {t("auth.useAnother")}
        </button>
      </div>
    );
  }

  if (state === "signedin") {
    return (
      <div className="wrap page" style={{ maxWidth: 520 }}>
        <h1 className="h1">{t("auth.alreadyIn")}</h1>
        <Link className="btn" style={{ marginTop: 24 }} to="/stock">{t("nav.stock")} →</Link>
      </div>
    );
  }

  return (
    <div className="wrap page" style={{ maxWidth: 520 }}>
      <p className="label">{t("auth.label")}</p>
      <h1 className="h1" style={{ marginTop: 12 }}>{t("auth.title")}</h1>
      <p className="muted" style={{ marginTop: 16, lineHeight: 1.7 }}>{t("auth.sub")}</p>

      {problem && (
        <div className="notice notice--warn" style={{ marginTop: 22 }}>
          {t(`auth.err.${problem}`)}
        </div>
      )}

      <form onSubmit={submit} style={{ marginTop: 26, display: "flex", flexDirection: "column", gap: 14 }}>
        <div className="field">
          <label htmlFor="auth-email">{t("enq.email")}</label>
          <input
            id="auth-email" className="input" type="email" required autoFocus
            autoComplete="email" inputMode="email" placeholder="vous@exemple.com"
            value={email} onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <button className="btn" type="submit" disabled={state === "sending" || !email.trim()}>
          {state === "sending" ? t("auth.sending") : t("auth.send")}
        </button>
      </form>

      <p className="tiny muted" style={{ marginTop: 22, lineHeight: 1.7 }}>{t("auth.why")}</p>
    </div>
  );
}
